import { FormEvent, useState } from "react";
import { supabase } from "../lib/supabase";
import Button from "../ui/Button";
import { IconAlertCircle } from "../ui/icons";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) setErr(error.message);
  };

  return (
    <div className="min-h-full grid md:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden md:flex relative overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.25) 0, transparent 55%)",
          }}
        />
        <div className="relative flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img
              src="/logo.svg"
              alt="Tennis for Life"
              className="h-12 w-auto [filter:brightness(0)_invert(1)]"
            />
          </div>
          <div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
              Content that writes,
              <br />
              reviews, and publishes
              <br />
              itself.
            </h1>
            <p className="mt-5 text-brand-100/90 max-w-sm text-sm leading-relaxed">
              The Tennis for Life Content Engine runs the academy's blog and
              social pipeline end-to-end — research, writing, imagery, review,
              and scheduled publication.
            </p>
          </div>
          <p className="text-xs text-brand-200/80">
            © Tennis for Life · Internal tool
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-sand-50">
        <form
          onSubmit={submit}
          className="w-full max-w-sm bg-white rounded-xl p-8 border border-ink-100 shadow-sm shadow-ink-900/5"
        >
          <div className="md:hidden flex items-center gap-3 mb-6">
            <img src="/logo.svg" alt="Tennis for Life" className="h-9 w-auto" />
          </div>
          <h2 className="font-display text-2xl font-bold text-ink-800 mb-1">
            Welcome back
          </h2>
          <p className="text-sm text-ink-500 mb-6">
            Sign in to continue to Content Engine.
          </p>

          <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-1.5">
            Email
          </label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-4 px-3 py-2.5 border border-ink-200 rounded-md text-sm focus:border-brand-500 focus:ring-0"
            placeholder="you@example.com"
          />
          <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-1.5">
            Password
          </label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-5 px-3 py-2.5 border border-ink-200 rounded-md text-sm focus:border-brand-500 focus:ring-0"
          />
          {err && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2">
              <IconAlertCircle size={16} className="text-red-600 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{err}</p>
            </div>
          )}
          <Button
            type="submit"
            variant="primary"
            size="lg"
            pending={busy}
            fullWidth
          >
            {busy ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
